import {
  blockCommunication, deleteCommunication,
  downloadExcel,
  downloadPDF,
  extendCommunicationDeadline, unblockCommunication,
} from "@/api/client/communications.js";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogOverlay } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { DataTableRowActions } from "@/components/ui/table/data-table-row-actions";
import Toast from "@/components/ui/toast.jsx";
import { calculateCommunicationDeadline } from "@/lib/communication-deadline";
import { useStore } from "@/store";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { t } from "i18next";
import { FileSpreadsheet, FileText, Pencil, UserPlus,Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { assignCommunications, getUser, updateUserCommunications } from "@/api/users/users-services";
import api from "@/api/api";
import { getCompany } from "@/api/company/company-services";
import { NavLink } from "react-router-dom";

// Aviso a las páginas de listado para que refresquen solo la página actual
// (sin recargar el navegador ni volver a la primera página).
const notifyCommunicationsChanged = (action) => {
  try {
    window.dispatchEvent(
      new CustomEvent("communications:refresh", { detail: { action } }),
    );
  } catch (err) {
    console.error("Error notificando el refresco de comunicaciones:", err);
  }
};
const actionsColumn = {
  id: "actions",
  accessorKey: "actions", // Añadir accessorKey para poder referenciarla en baseColumns
  header: ({ column }) => (
    <DataTableColumnHeader
      column={column}
      title={t("headColumns.communications.actions")}
    />
  ),
  cell: ({ row }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false); // Para el popup de eliminación
    const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false); // Para el popup de extensión de plazo
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false); // Para el popup de asignación
    const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false); // Para el popup de bloqueo
    const [reason, setReason] = useState("");
    const [usereditUsers, setUsereditUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialAssignedUsers, setInitialAssignedUsers] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoadingAssignDialog, setIsLoadingAssignDialog] = useState(false);
    const { id, type, extendedDate, isBlocked } = row.original;

    // Leer el usuario logueado y sus roles desde el store
    const loggedInUser = useStore((state) => state.loggedInUser);
    const userRoles = loggedInUser?.roles || [];
    const isSuperadmin = userRoles.includes("superadmin");

    // Determinar si se puede mostrar la acción "Asignar a Useredit"
    const canAssignToUseredit =
        userRoles.includes("admin") || userRoles.includes("superadmin");

    const handleBlock = async () => {
      try {
        await blockCommunication(id);
        console.log(`Comunicación ${id} bloqueada.`);
        setIsBlockDialogOpen(false); // Cierra el popup después de confirmar
        notifyCommunicationsChanged("block");
      } catch (error) {
        console.error(`Error al bloquear la comunicación ${id}:`, error);
      }
    };

    const handleUnblock = async () => {
      try {
        await unblockCommunication(id);
        console.log(`Comunicación ${id} desbloqueada.`);
        notifyCommunicationsChanged("unblock");
      } catch (error) {
        console.error(`Error al desbloquear la comunicación ${id}:`, error);
      }
    };

    const handleDeleteClick = () => setIsDialogOpen(true);

    const confirmDelete = async () => {
      try{
        await deleteCommunication(id, type);
        setIsDialogOpen(false);
        notifyCommunicationsChanged("delete");
      } catch (error){
        console.error(`Error al borrar la comunicación ${id}:`,error);
      }
    };

    const handleExtendDeadline = () => {
      if (reason.trim()) {
        extendCommunicationDeadline(id, reason)
            .then((response) => {
              if (response.status === 200) {
                setIsExtendDialogOpen(false);
                setReason("");
                setErrorMessage("");
                notifyCommunicationsChanged("extend");
              } else {
                setErrorMessage(t("messages.extendDeadlineFailure"));
              }
            })
            .catch(() => {
              setErrorMessage(t("messages.extendDeadlineFailure"));
            });
      }
    };

    const handleDownloadPDF = () => {
      downloadPDF(id)
          .then((data) => {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `communication_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          })
          .catch(() => {
            setErrorMessage(t("messages.downloadFailurePDF"));
          });
    };

    const handleDownloadExcel = () => {
      downloadExcel(id)
          .then((data) => {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `communication_${id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          })
          .catch(() => {
            setErrorMessage(t("messages.downloadFailureExcel"));
          });
    };

    const fetchUsereditUsers = async () => {
      setIsLoadingAssignDialog(true);
      try {
        const allUsers = await useStore.getState().getUsers();
        const filteredUsers = allUsers.filter((user) =>
            user.roles.includes("useredit"),
        );
        setUsereditUsers(filteredUsers);

        // Precargar usuarios ya asignados a esta comunicación
        const commResponse = await api.get(`/communications/${id}?language=es`);
        const communication = commResponse.data ?? commResponse;
        const assignedUsers = communication.users ?? [];
        const assignedIds = assignedUsers.map((u) => u.id);
        setSelectedUsers(assignedIds);
        setInitialAssignedUsers(assignedIds);
      } catch (err) {
        console.error("Error al cargar datos del diálogo de asignación:", err);
        // Si falla, al menos mostramos los usuarios useredit sin precarga
      } finally {
        setIsLoadingAssignDialog(false);
      }
    };

    const handleAssignToUseredit = async () => {
      for (const userId of selectedUsers) {
        try {
          // Llamada directa a la nueva función con el ID del usuario y de la comunicación
          await assignCommunications(userId, id);
        } catch (error) {
          console.error(`Error al asignar la comunicación ${id} al usuario ${userId}:`, error);
        }
      }
      // Retirar la comunicación de los usuarios desmarcados que estaban asignados
      const removedUserIds = initialAssignedUsers.filter(
        (userId) => !selectedUsers.includes(userId),
      );
      for (const userId of removedUserIds) {
        try {
          const userDetail = await getUser(userId);
          const currentIds = (userDetail?.communications ?? []).map((comm) =>
            typeof comm === "object" ? comm.id : comm,
          );
          const remainingIds = currentIds.filter(
            (communicationId) => communicationId !== id,
          );
          await updateUserCommunications(userId, remainingIds);
        } catch (error) {
          console.error(`Error al desasignar la comunicación ${id} del usuario ${userId}:`, error);
        }
      }
      setIsAssignDialogOpen(false);
      setSelectedUsers([]);
      setInitialAssignedUsers([]);
      notifyCommunicationsChanged("assign"); // Refrescar solo la página actual
    };

    useEffect(() => {
      if (isAssignDialogOpen) {
        fetchUsereditUsers();
      }
    }, [isAssignDialogOpen]);

    useEffect(() => {
      if (errorMessage) {
        const timer = setTimeout(() => setErrorMessage(""), 5000);
        return () => clearTimeout(timer);
      }
    }, [errorMessage]);

    if (isBlocked && !isSuperadmin) {
      return null;
    }

    return (
        <>
          {isBlocked && !isSuperadmin ? null : (
              <DataTableRowActions
                  row={row}
                  actions={[
                    ...(isBlocked && isSuperadmin
                        ? [
                          {
                            label: t("buttonTexts.unblock"),
                            icon: FileText,
                            onClick: handleUnblock,
                          },
                        ]
                        : [
                          {
                            label: t("buttonTexts.edit"),
                            icon: Pencil,
                            onClick: () => handleUpdate(id),
                          },
                          {
                            label: t("buttonTexts.downloadPDF"),
                            icon: FileText,
                            onClick: handleDownloadPDF,
                          },
                          {
                            label: t("buttonTexts.downloadExcel"),
                            icon: FileSpreadsheet,
                            onClick: handleDownloadExcel,
                          },
                          ...(extendedDate === null
                              ? [
                                {
                                  label: t("buttonTexts.extendDeadline"),
                                  icon: Pencil,
                                  onClick: () => setIsExtendDialogOpen(true),
                                },
                              ]
                              : []),
                          ...(canAssignToUseredit
                              ? [
                                {
                                  label: t("buttonTexts.assignToUseredit"),
                                  icon: UserPlus,
                                  onClick: () => setIsAssignDialogOpen(true),
                                },
                              ]
                              : []),
                          {
                            label: t("buttonTexts.block"),
                            icon: Pencil,
                            onClick: () => setIsBlockDialogOpen(true),
                          },
                          ...(isSuperadmin
                              ? [
                                {
                                  label: t("buttonTexts.delete"),
                                  icon: Trash,
                                  onClick: handleDeleteClick,
                                },
                              ]
                              : []),
                        ]),
                  ]}
              />
          )}
          {isDialogOpen && (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogOverlay />
                <DialogContent>
                  <h2>{t("alertDialog.delete.title")}</h2>
                  <p>{t("alertDialog.delete.description")}</p>
                  <div className="flex justify-center gap-4">
                    <button
                        onClick={() => setIsDialogOpen(false)}
                        className="bg-gray-300 px-4 py-2 rounded"
                    >
                      {t("alertDialog.cancel")}
                    </button>
                    <button
                        onClick={confirmDelete}
                        className="bg-red-500 text-white px-4 py-2 rounded"
                    >
                      {t("alertDialog.confirm")}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
          )}
          {isExtendDialogOpen && (
              <Dialog open={isExtendDialogOpen} onOpenChange={setIsExtendDialogOpen}>
                <DialogOverlay />
                <DialogContent>
                  <h2>{t("dialogTexts.extendDeadlineTitle")}</h2>
                  <Input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required={true}
                      placeholder={t("dialogTexts.extendDeadlinePlaceholder")}
                      className="mb-4"
                  />
                  {errorMessage && (
                      <p className="text-red-500 mb-4">{errorMessage}</p>
                  )}
                  <Button onClick={handleExtendDeadline} disabled={!reason.trim()}>
                    {t("buttonTexts.accept")}
                  </Button>
                  <Button
                      onClick={() => setIsExtendDialogOpen(false)}
                      className="ml-2"
                  >
                    {t("buttonTexts.cancel")}
                  </Button>
                </DialogContent>
              </Dialog>
          )}
          {isAssignDialogOpen && (
              <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                <DialogOverlay />
                <DialogContent>
                  <h2>{t("dialogTexts.assignToUsereditTitle")}</h2>
                  {isLoadingAssignDialog ? (
                      <p className="text-gray-500 py-4">{t("messages.loading")}</p>
                  ) : (
                      <>
                        <div className="flex flex-col gap-4">
                          {usereditUsers.map((user) => (
                              <div key={user.id} className="flex items-center gap-2">
                                <Checkbox
                                    checked={selectedUsers.includes(user.id)}
                                    onCheckedChange={(checked) =>
                                        setSelectedUsers((prev) =>
                                            checked
                                                ? [...prev, user.id]
                                                : prev.filter((id) => id !== user.id)
                                        )
                                    }
                                    aria-label={`Seleccionar ${user.name}`}
                                />
                                <span>{user.name}</span>
                              </div>
                          ))}
                        </div>
                        <div className="flex justify-end gap-4 mt-4">
                          <Button
                              onClick={handleAssignToUseredit}
                              disabled={
                                isLoadingAssignDialog ||
                                (selectedUsers.length === 0 &&
                                  initialAssignedUsers.length === 0)
                              }
                          >
                            {t("buttonTexts.confirmAssignment")}
                          </Button>
                          <Button onClick={() => setIsAssignDialogOpen(false)}>
                            {t("buttonTexts.cancel")}
                          </Button>
                        </div>
                      </>
                  )}
                </DialogContent>
              </Dialog>
          )}
          {isBlockDialogOpen && (
              <Dialog open={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
                <DialogOverlay />
                <DialogContent>
                  <h2>{t("dialogTexts.confirmBlockTitle")}</h2>
                  <p>{t("dialogTexts.confirmBlockDescription")}</p>
                  <div className="flex justify-center gap-4">
                    <Button
                        onClick={handleBlock}
                        className="bg-red-500 text-white px-4 py-2 rounded"
                    >
                      {t("buttonTexts.confirmBlock")}
                    </Button>
                    <Button
                        onClick={() => setIsBlockDialogOpen(false)}
                        className="bg-gray-300 px-4 py-2 rounded"
                    >
                      {t("buttonTexts.cancel")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
          )}
          <Toast message={errorMessage} onClose={() => setErrorMessage("")} />
        </>
    );
  },
};

// Columnas base estáticas (ahora incluye la columna de acciones)
const baseColumns = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    cell: ({ row }) => (
      <div className="text-center">{row.getValue("id")}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "identificationNumber",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.trackingNumber")}
      />
    ),
    cell: ({ row }) => (
      <div className="text-center">
        {row.getValue("identificationNumber") || "-"}
      </div>
    ),
    filterFn: "includesString",
    enableSorting: true,
  },
  {
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.communicationType")}
      />
    ),
    cell: ({ row }) => {
      const type = row.getValue("type");
      const translatedType = t(`communicationTypes.${type}`, type);
      return (
        <Badge key={`type-${row.id}`} className="flex justify-center items-center mx-auto text-center">
          {translatedType}
        </Badge>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.createDate")}
      />
    ),
    cell: ({ row }) => {
      const dateValue = row.getValue("date");
      const formattedDate = dateValue
        ? isValid(parseISO(dateValue))
          ? format(parseISO(dateValue), "dd-MM-yyyy", { locale: es })
          : "Fecha no válida"
        : "Fecha no válida";

      return <div className="text-center">{formattedDate}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "limitDate",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.limitDate")}
      />
    ),
	    cell: ({ row }) => {
	      const companyData = useStore((state) => state.companyData);
	      const deadline = calculateCommunicationDeadline(row.original, companyData);

	      if (!deadline) {
	        return <div className="text-center">Fecha no válida</div>;
	      }

	      return (
	        <div className={`p-2 rounded ${deadline.colors.bg} ${deadline.colors.text} text-center`}>
	          {deadline.days} días
	        </div>
	      );
	    },
    enableSorting: true,
  },
  {
    accessorKey: "state",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.state")}
      />
    ),
    cell: ({ row }) => {
      const status = row.getValue("state");
      const selectedLanguage = useStore((state) => state.selectedLanguage);
      const [statusText, setStatusText] = useState("");

      useEffect(() => {
        const text =
          status?.names.find((name) => name.language === selectedLanguage)
            ?.text || "no lang";
        setStatusText(text);
      }, [selectedLanguage, status?.names]);

      return (
        <div className="flex w-full h-full items-center justify-center">
          <div
            className="flex items-center justify-center px-2 py-1 rounded text-center"
            style={{
              backgroundColor: status?.color || "transparent",
              color: "white",
            }}
          >
            {status?.icon && (
              <status.icon className="mr-2 h-4 w-4 text-muted-foreground" />
            )}
            <span>{statusText}</span>
          </div>
        </div>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: "risk",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("headColumns.communications.priority")}
      />
    ),
    cell: ({ row }) => {
      const risk = row.getValue("risk");
      const translatedRisk = t(`risk.${risk}`)
      return (
        <div className="text-center">{translatedRisk}</div>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: "source",
    header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("headColumns.communications.source")} />
    ),
    cell: ({ row }) => {
      const sourceValue = row.getValue("source") || 'public';
      const translatedSource = t(`sources.${sourceValue}`);
      return (
          <div className="text-center">{translatedSource}</div>
      );
    },
    enableSorting: true,
    filterFn: "includesString", // Opcional: agrega filtros si es necesario
  },
  {
    accessorKey: "tags",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t("headColumns.communications.tags")} />
    ),
    cell: ({ row }) => {
      const tags = row.getValue("tags") || [];
      return (
        <div className="flex flex-wrap justify-center gap-1">
          {tags.map((tag, index) => (
            <Badge key={`${tag.id}-${index}`} className="bg-blue-500 text-white">
              {tag.text}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: "solveDate",
    header: ({ column }) => (
        <DataTableColumnHeader
            column={column}
            title={t("headColumns.communications.solveDate")}
        />
    ),
    cell: ({ row }) => {
      const solveDate = row.getValue("solveDate");
      const formattedDate = solveDate
          ? isValid(parseISO(solveDate))
              ? format(parseISO(solveDate), "dd-MM-yyyy", { locale: es })
              : t("messages.invalidDate")
          : "";

      return <div className="text-center">{formattedDate}</div>;
    },
    enableSorting: true,
  },
  // Añadir la columna de acciones como columna base (ahora actionsColumn ya está definido)
  actionsColumn,
];

const findAnswerForQuestion = (communication, question) => {
  if (!communication || !Array.isArray(communication.answers)) {
    return undefined;
  }
  return communication.answers.find(
    (a) =>
      (question?.id !== null &&
        question?.id !== undefined &&
        (a?.questionId === question.id || a?.question?.id === question.id)) ||
      (question?.alias && a?.question?.alias === question.alias),
  );
};

const generateDynamicColumns = (
  complaints,
  selectedLanguage,
  questionsCatalog = null,
) => {
  try {
    if (!Array.isArray(complaints) || complaints.length === 0) {
      // Sin filas en la página, las columnas estables vienen del catálogo.
      if (!Array.isArray(questionsCatalog)) {
        return [];
      }
    }

    let visibleQuestions = [];

    if (Array.isArray(questionsCatalog)) {
      /* Contrato compacto: el catálogo trae una definición por pregunta del
         tipo, independiente de la página. Las columnas no desaparecen con
         página vacía ni cuando una pregunta no tiene respuesta en la página. */
      visibleQuestions = questionsCatalog.filter(
        (question) => question?.visibility?.list === true,
      );
    } else {
      /* Contrato legacy: recopilamos las preguntas visibles
         (visibility.list === true) a partir de las respuestas (answers)
         presentes en todas las comunicaciones. */
      const visibleQuestionsMap = new Map();

      complaints.forEach((complaint) => {
        try {
          if (Array.isArray(complaint?.answers)) {
            complaint.answers.forEach((answer) => {
              const question = answer?.question;
              if (question?.visibility?.list === true) {
                // Si aún no hemos añadido esta pregunta (alias único) la guardamos
                if (!visibleQuestionsMap.has(question.alias)) {
                  visibleQuestionsMap.set(question.alias, question);
                }
              }
            });
          }

          // Incluir preguntas visibles directamente desde el formulario
          if (Array.isArray(complaint?.form?.questions)) {
            complaint.form.questions.forEach((question) => {
              if (question?.visibility?.list === true) {
                if (!visibleQuestionsMap.has(question.alias)) {
                  visibleQuestionsMap.set(question.alias, question);
                }
              }
            });
          }
        } catch (err) {
          console.error("Error procesando respuestas de comunicación:", err);
        }
      });

      visibleQuestions = Array.from(visibleQuestionsMap.values());
    }

    // Crear columnas dinámicas para cada pregunta visible
    const dynamicColumns = visibleQuestions
      .map((question) => {
        try {
          const questionText =
            question?.texts?.find((text) => text?.language === selectedLanguage)?.text ||
            question?.texts?.[0]?.text ||
            question?.alias ||
            "Pregunta";

          return {
            accessorKey: question.alias,
            order: question.order ?? 999, // Incluir el orden de la pregunta
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={questionText} />
            ),
            cell: ({ row }) => {
              try {
                // Buscar la respuesta para esta pregunta en la comunicación actual
                const communication = complaints.find((c) => c?.id === row.original?.id);
                if (!communication || !Array.isArray(communication.answers)) {
                  return <div className="text-center">-</div>;
                }

                const answer = findAnswerForQuestion(communication, question);

                if (!answer || !answer.answerText) {
                  return <div className="text-center">-</div>;
                }

                // Formatear respuesta según el tipo de la pregunta
                let formattedAnswer = answer.answerText;

                // Verificar si la pregunta tiene opciones
                if (question.options && Array.isArray(question.options) && question.options.length > 0) {
                  // Buscar la opción cuyo value coincida con answerText
                  const selectedOption = question.options.find((opt) => opt?.value === answer.answerText);
                  if (selectedOption && selectedOption.text && Array.isArray(selectedOption.text)) {
                    // Buscar el texto en el idioma seleccionado
                    const translatedText = selectedOption.text.find((t) => t?.language === selectedLanguage);
                    // Si no encuentra el idioma seleccionado, usar el primer texto disponible
                    formattedAnswer = translatedText?.text || selectedOption.text[0]?.text || answer.answerText;
                  }
                } else if (question.type === "terms") {
                  formattedAnswer = answer.answerText === "1" ? "Sí" : "No";
                }

                return <div className="text-center">{formattedAnswer}</div>;
              } catch (err) {
                console.error("Error renderizando celda:", err);
                return <div className="text-center">-</div>;
              }
            },
          };
        } catch (err) {
          console.error("Error creando columna dinámica:", err);
          return null;
        }
      })
      .filter(Boolean); // Eliminar columnas nulas

    return dynamicColumns;
  } catch (err) {
    console.error("Error generando columnas dinámicas:", err);
    return [];
  }
};

const handleUpdate = (id) => {
  window.location.href = `/admin/edit/${id}`;
};

export const getColumns = (complaints, selectedLanguage, questionsCatalog = null) => {
  try {
    console.log("🔍 getColumns: Ejecutándose con complaints:", complaints?.length || 0);
    
    // Garantizamos que complaints sea siempre un array
    const safeComplaints = Array.isArray(complaints) ? complaints : [];

    // 1. Columnas dinámicas derivadas de las preguntas visibles del formulario
    const dynamicColumns =
      safeComplaints.length > 0 || Array.isArray(questionsCatalog)
        ? generateDynamicColumns(safeComplaints, selectedLanguage, questionsCatalog)
        : [];

    // 2. Obtenemos la configuración de columnas base definida en la empresa
    const companyData = useStore.getState().companyData || {};
    console.log("🔍 getColumns: companyData del store:", companyData);
    console.log("🔍 getColumns: baseColumns del store:", companyData.baseColumns);
    const baseColumnsConfig = companyData.baseColumns || null;

    let processedBaseColumns = [];

    if (baseColumnsConfig && Object.keys(baseColumnsConfig).length > 0) {
      // Filtramos solo las columnas incluidas en la configuración
      // Las ordenamos según el valor (order) definido
      processedBaseColumns = baseColumns
        .filter((col) => baseColumnsConfig.hasOwnProperty(col.accessorKey))
        .map((col) => ({
          ...col,
          order: baseColumnsConfig[col.accessorKey] || 999
        }))
        .sort(
          (a, b) =>
            (a.order || 999) - (b.order || 999)
        );
    } else {
      // Si no hay configuración usamos todas las columnas base por defecto
      processedBaseColumns = baseColumns.map((col, index) => ({
        ...col,
        order: index + 1
      }));
    }

    // 3. Combinamos todas las columnas (base y dinámicas) y las ordenamos por posición
    const allColumns = [...processedBaseColumns, ...dynamicColumns];
    return allColumns.sort((a, b) => (a.order || 999) - (b.order || 999));
  } catch (error) {
    console.error("Error generating columns:", error);
    // En caso de error devolvemos al menos las columnas base por defecto
    return baseColumns;
  }
};
